/**
 * Demo personas (19 Oct): the setup page for the 22 Oct access demo.
 *
 * Who plays which role (each "Demo as" button turns THIS tab into that
 * person), someone else (read-only unless a "[UAT TEST]" person), and the
 * demo's UAT data: prepare (teams, HR group, approver and site-owner cover)
 * and reset (cancel the demo case; optionally undo the setup); and (22 Oct)
 * "Create a ready demo case": prepare, then a fresh New user past the gate,
 * waiting in Backoffice, as the demo HR person; and (28 Oct) "File as the
 * HR Portal" (`hr-portal-card.tsx`). Test systems
 * only, for admins on VIEW_AS_EMAILS; the server refuses everyone else.
 */

import { Alert, Badge, Button, Checkbox, cn, Field, Input, Typography } from "@norican/react";
import { useState } from "react";
import { Link } from "react-router";

import { ErrorState, PageContainer, SectionCard } from "@/components/common";
import { EmployeePicker } from "@/features/tickets";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import { type DemoSteps, useMe } from "@/lib/api";
import { DEMO_ROLE_COLOUR, type DemoRole, mayOfferDemoAs } from "@/lib/demo-persona";

import {
  useCreateDemoCase,
  useDemoPersonas,
  usePrepareDemo,
  useResetDemo,
  useStartDemoPersona,
} from "./api";
import { HrPortalCard } from "./hr-portal-card";

function StepList({ result }: { result: DemoSteps }) {
  const { t } = useT();
  return (
    <div className="flex flex-col gap-2">
      <Alert tone={result.ok ? "success" : "warning"}>
        {result.ok ? t("demoPersona.page.allOk") : t("demoPersona.page.someNot")}
      </Alert>
      <ul className="flex flex-col gap-1 text-sm">
        {result.steps.map((step) => (
          <li key={step.key} className="flex gap-2">
            <Badge tone={step.ok ? "success" : "warning"} variant="subtle" size="xs">
              {step.ok ? t("demoPersona.page.ok") : t("demoPersona.page.notOk")}
            </Badge>
            <span>{step.detail}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DemoPersonaPage() {
  const { t } = useT();
  const { t: tt } = useT("errors");
  useDocumentTitle(t("demoPersona.page.title"));
  const { data: me } = useMe();
  const offered = mayOfferDemoAs(me);
  const cast = useDemoPersonas(offered);
  const start = useStartDemoPersona();
  const prepare = usePrepareDemo();
  const reset = useResetDemo();
  const demoCase = useCreateDemoCase();
  const [otherId, setOtherId] = useState<string | null>(null);
  const [otherName, setOtherName] = useState<string | null>(null);
  const [caseNumber, setCaseNumber] = useState("");
  const [undoSetup, setUndoSetup] = useState(false);

  if (!offered) {
    return (
      <PageContainer title={t("demoPersona.page.title")}>
        <Alert tone="neutral">{tt("code.ADMIN_REQUIRED")}</Alert>
      </PageContainer>
    );
  }
  const play = (employeeId: string, role: DemoRole) => start.mutate({ employeeId, role });

  return (
    <PageContainer title={t("demoPersona.page.title")}>
      <div className="flex flex-col gap-6">
        <Typography variant="body-sm" tone="muted">
          {t("demoPersona.page.intro")}
        </Typography>

        <SectionCard title={t("demoPersona.page.cast")}>
          {cast.isError ? <ErrorState variant="inline" error={cast.error} /> : null}
          <ul className="flex flex-col gap-3">
            {(cast.data?.items ?? []).map((p) => (
              <li key={p.role} className="flex flex-wrap items-center gap-3">
                <span
                  aria-hidden="true"
                  className={cn("inline-block size-3 rounded-full", DEMO_ROLE_COLOUR[p.role])}
                />
                <span className="w-32 font-semibold">{t(`demoPersona.role.${p.role}`)}</span>
                <span className="min-w-0 flex-1">
                  {p.name}
                  <span className="text-fg-muted ml-2 text-sm">{p.email}</span>
                </span>
                <Badge tone={p.can_act ? "success" : "neutral"} variant="subtle" size="xs">
                  {p.can_act ? t("demoPersona.acts") : t("demoPersona.readOnly")}
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  tone="neutral"
                  disabled={!p.employee_id || start.isPending}
                  onClick={() => p.employee_id && play(p.employee_id, p.role)}
                >
                  {p.employee_id ? t("demoPersona.page.play") : t("demoPersona.page.notInItsm")}
                </Button>
              </li>
            ))}
          </ul>
          {cast.data ? (
            <Typography variant="caption" tone="muted" className="mt-3 block">
              {t("demoPersona.page.newHire", { name: cast.data.new_hire.name })}
            </Typography>
          ) : null}
          {start.isError ? (
            <ErrorState variant="inline" error={start.error} title={t("demoPersona.page.failed")} />
          ) : null}
        </SectionCard>

        <SectionCard title={t("demoPersona.page.other")}>
          <div className="flex flex-col gap-3">
            <Typography variant="body-sm" tone="muted">
              {t("demoPersona.page.otherIntro")}
            </Typography>
            <Field name="demo-other-person" label={t("demoPersona.page.person")}>
              <EmployeePicker
                name="demo-other-person"
                value={otherId}
                defaultDisplayValue={otherName}
                onChange={(entry) => {
                  setOtherId(entry?.id ?? null);
                  setOtherName(entry?.display_name ?? null);
                }}
                aria-label={t("demoPersona.page.person")}
              />
            </Field>
            <div>
              <Button
                size="sm"
                disabled={otherId === null || start.isPending}
                onClick={() => otherId && play(otherId, "other")}
              >
                {t("demoPersona.page.play")}
              </Button>
            </div>
          </div>
        </SectionCard>

        <SectionCard title={t("demoPersona.page.prepare")}>
          <div className="flex flex-col gap-3">
            <Typography variant="body-sm" tone="muted">
              {t("demoPersona.page.prepareIntro")}
            </Typography>
            <div>
              <Button size="sm" loading={prepare.isPending} onClick={() => prepare.mutate()}>
                {t("demoPersona.page.prepareButton")}
              </Button>
            </div>
            {prepare.isError ? <ErrorState variant="inline" error={prepare.error} /> : null}
            {prepare.data ? <StepList result={prepare.data} /> : null}
          </div>
        </SectionCard>

        <SectionCard title={t("demoPersona.page.demoCase")}>
          <div className="flex flex-col gap-3">
            <Typography variant="body-sm" tone="muted">
              {t("demoPersona.page.demoCaseIntro")}
            </Typography>
            <div>
              <Button size="sm" loading={demoCase.isPending} onClick={() => demoCase.mutate()}>
                {t("demoPersona.page.demoCaseButton")}
              </Button>
            </div>
            {demoCase.isError ? <ErrorState variant="inline" error={demoCase.error} /> : null}
            {demoCase.data?.case_number ? (
              <Alert tone={demoCase.data.created ? "success" : "warning"}>
                {demoCase.data.created
                  ? t("demoPersona.page.demoCaseReady", { number: demoCase.data.case_number })
                  : t("demoPersona.page.demoCaseExists", {
                      number: demoCase.data.case_number,
                    })}{" "}
                {demoCase.data.url_path ? (
                  <Link to={demoCase.data.url_path} className="underline">
                    {t("demoPersona.page.demoCaseOpen", { number: demoCase.data.case_number })}
                  </Link>
                ) : null}
              </Alert>
            ) : null}
            {demoCase.data ? <StepList result={demoCase.data} /> : null}
          </div>
        </SectionCard>

        <HrPortalCard />

        <SectionCard title={t("demoPersona.page.reset")}>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              reset.mutate({ caseNumber: caseNumber.trim(), undoSetup });
            }}
          >
            <Typography variant="body-sm" tone="muted">
              {t("demoPersona.page.resetIntro")}
            </Typography>
            <Field name="demo-case-number" label={t("demoPersona.page.caseNumber")}>
              <Input
                value={caseNumber}
                onChange={(event) => setCaseNumber(event.target.value)}
                maxLength={32}
                inputMode="numeric"
              />
            </Field>
            <Checkbox.Root
              checked={undoSetup}
              onCheckedChange={(next) => setUndoSetup(next === true)}
              label={t("demoPersona.page.undoSetup")}
            />
            <div>
              <Button
                type="submit"
                size="sm"
                tone="danger"
                loading={reset.isPending}
                disabled={caseNumber.trim() === "" && !undoSetup}
              >
                {t("demoPersona.page.resetButton")}
              </Button>
            </div>
            {reset.isError ? <ErrorState variant="inline" error={reset.error} /> : null}
            {reset.data ? <StepList result={reset.data} /> : null}
          </form>
        </SectionCard>
      </div>
    </PageContainer>
  );
}
